import React from 'react'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import {
  getFormattedDateAndTime,
  SortDirectionEnum,
  TestResult,
  useStateWithDebounce,
  TestType,
  useTestsData,
  Lab,
  UserPermissions,
  CompanyMemberRole,
  GroupMemberRole,
  useAuth,
} from '@modmd/data'
import { useSortChangeHandler } from 'utils/helpers'
import { ROUTES } from 'internal-portal/constants/routes'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { TestsDatalist } from 'sharedComponents/TestsDatalist'
import { Box, Container } from 'components/Layout'
import Icon from 'components/Icon'
import TextInput from 'components/TextInput'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { SelectInput } from 'components/SelectInput'
import { DateInput } from 'components/DateInput'
import Button from 'components/Button'
import { CompaniesDropdown } from 'sharedComponents/CompaniesDropdown'
import { GroupsDropdown } from 'sharedComponents/GroupsDropdown'
import { COLOR, DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { endOfDay, startOfDay } from 'date-fns'
import { LAB_OPTIONS, TYPE_OPTIONS } from '../TestSessions/utils'

type TestsProps = RouteComponentProps

type FilterState = {
  location?: string
  page?: string
  direction?: SortDirectionEnum
  sortBy?: string
  dateFrom?: string
  dateTo?: string
  memberLike?: string
  companyOrGroupLike?: string
  user?: string
  result?: TestResult | 'pending'
  resultVerified?: string
  resultVerifiedAt?: string
  resultManuallyEntered?: string
  companyId?: string
  groupId?: string
  barcode?: string
  lab?: Lab
  type?: TestType
}

const TestsList: React.VFC<TestsProps> = () => {
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const { getUserCompaniesWithRole, getUserGroupsWithRole, hasPermission } = useAuth()
  const history = useHistory()
  const [isCollapsed, setCollapse] = React.useState(false)
  const searchState = useSearchState<FilterState>()

  const { companies } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner],
  })
  const { groups } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Coordinator, GroupMemberRole.Manager],
  })

  const getCompanyIds = () => {
    if (searchState.data.companyId) return [searchState.data.companyId]
    if (
      !hasPermission([UserPermissions.SEE_FULL_TEST_SESSIONS]) &&
      hasPermission([UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS])
    )
      return companies.length ? companies.map(({ id }) => id) : undefined
    return undefined
  }

  const getGroupIds = () => {
    if (searchState.data.groupId) return [searchState.data.groupId]
    if (
      !companies.length &&
      !hasPermission([UserPermissions.SEE_FULL_TEST_SESSIONS]) &&
      hasPermission([UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS])
    )
      return groups.length ? groups.map(({ id }) => id) : undefined

    return undefined
  }

  const { sorter, handleSortChange } = useSortChangeHandler()
  const testsQuery = useTestsData({
    filter: {
      hasResult: searchState.data.result ? searchState.data.result !== 'pending' : undefined,
      result: Object.values(TestResult).some((result) => result === searchState.data.result)
        ? (searchState.data.result as TestResult)
        : undefined,
      resultVerified: searchState.data.resultVerified ? true : undefined,
      resultVerifiedAt: searchState.data.resultVerifiedAt,
      resultManuallyEntered: searchState.data.resultManuallyEntered ? true : undefined,
      memberLike: searchState.data.memberLike,
      barcode: searchState.data.barcode,
      lab: searchState.data.lab || undefined,
      type: searchState.data.type || undefined,
      location: searchState.data.location || undefined,
      companyOrGroupNameLike: searchState.data.companyOrGroupLike || undefined,
      testedBetween: [
        searchState.data.dateFrom
          ? startOfDay(new Date(searchState.data.dateFrom)).toISOString()
          : null,
        searchState.data.dateTo ? endOfDay(new Date(searchState.data.dateTo)).toISOString() : null,
      ],
      groupIds: getGroupIds(),
      companyIds: getCompanyIds(),
    },
    sorter,
    page: Number(searchState.data.page) || 1,
  })
  const [filterData, setFilterData] = useStateWithDebounce<FilterState>({}, (filter) => {
    searchState.setSearchState({ ...filter, page: 1 }, false, true)
  })

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      setFilterData({ ...filterData, [name]: value })
    },
    [filterData, setFilterData]
  )

  return (
    <DashboardPage>
      <Container>
        <TestsDatalist
          tests={testsQuery.data?.tests.tests || []}
          pagination={testsQuery.data?.tests.pagination}
          isLoading={testsQuery.loading}
          tableHeader={
            <Box
              display="grid"
              gridGap="1rem"
              gridTemplateColumns={
                isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(13rem, max-content))'
              }
            >
              <TextInput
                name="memberLike"
                label="Member"
                value={filterData.memberLike || ''}
                onChange={handleInputChange}
                leftIcon={<Icon.Search />}
                placeholder="Search by member name"
                hasBottomContent
              />
              <SelectInput
                name="result"
                label="Result"
                value={filterData.result || ''}
                onChange={handleInputChange}
                options={[
                  { value: '', label: 'All' },
                  ...Object.values(TestResult).map((result) => ({
                    value: result,
                    label: result,
                  })),
                  { value: 'pending', label: 'Pending' },
                ]}
                hasBottomContent
              />
              <TextInput
                name="location"
                label="Location"
                value={filterData.location || ''}
                onChange={handleInputChange}
                leftIcon={<Icon.Search />}
                placeholder="Search by Location"
                hasBottomContent
              />
              <DateInput
                id="dateFrom"
                name="dateFrom"
                label="Date From"
                value={filterData.dateFrom ? new Date(filterData.dateFrom) : null}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateFrom: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              <DateInput
                id="dateTo"
                name="dateTo"
                label="Date To"
                value={filterData.dateTo ? new Date(filterData.dateTo) : null}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateTo: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              {isCollapsed ? (
                <>
                  <CompaniesDropdown
                    value={searchState.data.companyId}
                    onChange={(id) => {
                      setFilterData({ ...filterData, companyId: String(id), groupId: '' })
                    }}
                    hasBottomContent
                  />
                  <GroupsDropdown
                    value={searchState.data.groupId}
                    onChange={(id) => {
                      setFilterData({ ...filterData, groupId: String(id) })
                    }}
                    companyId={searchState.data.companyId}
                    hasBottomContent
                  />
                  <TextInput
                    name="barcode"
                    label="Barcode"
                    value={filterData.barcode || ''}
                    onChange={handleInputChange}
                    leftIcon={<Icon.Search />}
                    placeholder="Search by Barcode"
                    hasBottomContent
                  />
                  <SelectInput
                    name="lab"
                    label="Lab"
                    value={filterData.lab || ''}
                    onChange={handleInputChange}
                    options={LAB_OPTIONS}
                    hasBottomContent
                  />
                  <SelectInput
                    name="type"
                    label="Type"
                    value={filterData.type || ''}
                    onChange={handleInputChange}
                    options={TYPE_OPTIONS}
                    hasBottomContent
                  />
                </>
              ) : null}
              <Button onClick={() => setCollapse(!isCollapsed)} appearance="link" size="medium">
                {!isCollapsed ? (
                  <>
                    <Icon.ChevronDown color={COLOR.text} />
                    Show more filters
                  </>
                ) : (
                  <>
                    <Icon.ChevronUp color={COLOR.text} />
                    Hide filters
                  </>
                )}
              </Button>
            </Box>
          }
          columnLabels={{
            location: null,
          }}
          sorter={sorter}
          onSortChange={handleSortChange}
          requestDebounceInMs={250}
          onRowClick={({ id }) => history.push(`/${ROUTES.TESTS}/${id}`)}
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          pageLength={testsQuery.data?.tests.pagination.length}
        />
      </Container>
    </DashboardPage>
  )
}

export default TestsList
